import React, { useCallback, useEffect, useState } from 'react';
import { Modal, Form, Tabs, Tab, Table } from 'react-bootstrap';
import { Button, ButtonWithLoading } from '@spark/ui-components/components/Button';
import { notification } from '@spark/ui-components/utils/notification';
import { exportarLayoutImportacaoDepositoNotas, importarDepositoNotasFiscais } from '@spark/services/deposito';

const layoutColumns = [
  { posicao: 1, nome: 'NumeroNF', descricao: 'Numero da NF (obrigatorio)', obrigatorio: true },
  { posicao: 2, nome: 'SerieNF', descricao: 'Serie da NF', obrigatorio: false },
  { posicao: 3, nome: 'CNPJCliente', descricao: 'CNPJ do cliente (14 digitos) (obrigatorio)', obrigatorio: true },
  { posicao: 4, nome: 'NomeDestinatario', descricao: 'Nome do destinatario', obrigatorio: false },
  { posicao: 5, nome: 'NomeRemetente', descricao: 'Nome do remetente', obrigatorio: false },
  { posicao: 6, nome: 'NomeResponsavelComercial', descricao: 'Responsavel comercial', obrigatorio: false },
  { posicao: 7, nome: 'NomeResponsavelLancamento', descricao: 'Responsavel lancamento', obrigatorio: false },
  { posicao: 8, nome: 'NomeResponsavelFechamento', descricao: 'Responsavel fechamento', obrigatorio: false },
  { posicao: 9, nome: 'DataChegada', descricao: 'Data chegada (DD/MM/YYYY)', obrigatorio: false },
  { posicao: 10, nome: 'DataLeitura', descricao: 'Data leitura (DD/MM/YYYY)', obrigatorio: false },
  { posicao: 11, nome: 'DataConferencia', descricao: 'Data conferencia (DD/MM/YYYY)', obrigatorio: false },
  { posicao: 12, nome: 'DataArmazenagem', descricao: 'Data armazenagem (DD/MM/YYYY)', obrigatorio: false },
  { posicao: 13, nome: 'DataAgendamento', descricao: 'Data agendamento (DD/MM/YYYY)', obrigatorio: false },
  { posicao: 14, nome: 'DataSaida', descricao: 'Data saida (DD/MM/YYYY)', obrigatorio: false },
  { posicao: 15, nome: 'QuantidadeTotalEtiquetas', descricao: 'Qtde etiquetas (numero)', obrigatorio: false },
  { posicao: 16, nome: 'QuantidadeTotalCaixas', descricao: 'Qtde caixas (numero)', obrigatorio: false },
  { posicao: 17, nome: 'QuantidadeTotalPaletes', descricao: 'Qtde paletes (numero)', obrigatorio: false },
  { posicao: 18, nome: 'ValorUnPallet', descricao: 'Valor unitario pallet (decimal)', obrigatorio: false },
  { posicao: 19, nome: 'ValorUnEtiqueta', descricao: 'Valor unitario etiqueta (decimal)', obrigatorio: false },
  { posicao: 20, nome: 'ValorUnArmazenagemPallet', descricao: 'Valor arm. pallet (decimal)', obrigatorio: false },
  { posicao: 21, nome: 'Freetime', descricao: 'Dias de freetime (numero)', obrigatorio: false },
  { posicao: 22, nome: 'Observacoes', descricao: 'Observacoes', obrigatorio: false },
  { posicao: 23, nome: 'CobrancaPaletizacaoRealizada', descricao: 'True/False ou 1/0', obrigatorio: false },
  { posicao: 24, nome: 'CobrancaEtiquetagemRealizada', descricao: 'True/False ou 1/0', obrigatorio: false },
  { posicao: 25, nome: 'CobrancaArmazenagemRealizada', descricao: 'True/False ou 1/0', obrigatorio: false },
  { posicao: 26, nome: 'Finalizado', descricao: 'True/False ou 1/0', obrigatorio: false }
];

const ImportarDepositoNotasModal = ({ show, onClose, onImportado }) => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [exportingLayout, setExportingLayout] = useState(false);
  const [activeTab, setActiveTab] = useState('upload');
  const [importResult, setImportResult] = useState(null);

  useEffect(() => {
    if (!show) {
      setFile(null);
      setUploading(false);
      setExportingLayout(false);
      setActiveTab('upload');
      setImportResult(null);
    }
  }, [show]);

  const handleClose = useCallback(() => {
    if (uploading) return;
    onClose?.();
  }, [onClose, uploading]);

  const handleExportLayout = useCallback(async () => {
    try {
      setExportingLayout(true);
      const response = await exportarLayoutImportacaoDepositoNotas();
      const blob = response instanceof Blob
        ? response
        : new Blob([response], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'layout-importacao-deposito-nota-fiscal.xlsx';
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      notification.error('Erro ao exportar layout.');
      console.error('Erro ao exportar layout:', error);
    } finally {
      setExportingLayout(false);
    }
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!file) {
      notification.error('Selecione um arquivo Excel para importar.');
      return;
    }

    const fileName = file.name.toLowerCase();
    const validExtensions = ['.xls', '.xlsx'];
    const hasValidExtension = validExtensions.some((ext) => fileName.endsWith(ext));

    if (!hasValidExtension) {
      notification.error('Selecione um arquivo Excel (.xls ou .xlsx).');
      return;
    }

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', file);

      const response = await importarDepositoNotasFiscais(formData);
      const responseData = response?.data || response;
      setImportResult(responseData);

      const mensagem =
        `Importacao concluida! Total: ${responseData.totalLinhas || 0} | ` +
        `Inseridos: ${responseData.inseridos || 0} | ` +
        `Atualizados: ${responseData.atualizados || 0} | ` +
        `Ignorados: ${responseData.ignorados || 0}` +
        (responseData.totalErros > 0 ? ` | Erros: ${responseData.totalErros}` : '');

      notification.success(mensagem);
      onImportado?.();
    } catch (error) {
      const message = error?.response?.data?.error || error?.message || 'Erro ao importar arquivo.';
      notification.error(message);
      console.error('Erro ao importar deposito:', error);
      setImportResult(null);
    } finally {
      setUploading(false);
    }
  }, [file, onImportado]);

  return (
    <Modal show={show} onHide={handleClose} centered backdrop="static" size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Importar Deposito Nota Fiscal</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="mb-3">
          <Tab eventKey="upload" title="Upload">
            <Form>
              <Form.Group controlId="excelArquivo" className="mb-3">
                <Form.Label>Arquivo Excel*</Form.Label>
                <Form.Control
                  type="file"
                  accept=".xls,.xlsx"
                  onChange={(event) => {
                    setFile(event.target.files?.[0] || null);
                    setImportResult(null);
                  }}
                  disabled={uploading}
                />
                <Form.Text className="text-muted">
                  Selecione um arquivo Excel (.xls ou .xlsx) com o layout de deposito.
                </Form.Text>
              </Form.Group>
            </Form>

            {importResult && (
              <div className="mt-4">
                <h6 className="mb-3">Resultado da Importacao</h6>
                <pre
                  style={{
                    backgroundColor: '#f5f5f5',
                    padding: '15px',
                    borderRadius: '4px',
                    overflow: 'auto',
                    maxHeight: '400px',
                    fontSize: '12px',
                    lineHeight: '1.5',
                    border: '1px solid #dee2e6'
                  }}
                >
                  {JSON.stringify(importResult, null, 2)}
                </pre>
              </div>
            )}
          </Tab>

          <Tab eventKey="layout" title="Layout do Excel">
            <div className="mb-3">
              <p className="text-muted">
                O arquivo deve conter as colunas abaixo na primeira linha (cabecalho).
              </p>
              <Table striped bordered hover size="sm">
                <thead>
                  <tr>
                    <th style={{ width: '80px' }}>Posicao</th>
                    <th>Nome da Coluna</th>
                    <th>Descricao</th>
                  </tr>
                </thead>
                <tbody>
                  {layoutColumns.map((col) => (
                    <tr key={col.posicao}>
                      <td className="text-center"><strong>{col.posicao}</strong></td>
                      <td>
                        {col.nome}
                        {col.obrigatorio && <span className="text-danger ms-1">*</span>}
                      </td>
                      <td>{col.descricao}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              <div className="alert alert-info mt-3">
                <strong>Observacoes:</strong>
                <ul className="mb-0 mt-2">
                  <li>Datas no formato DD/MM/YYYY</li>
                  <li>Valores decimais podem usar virgula ou ponto</li>
                  <li>Colunas marcadas com * sao obrigatorias</li>
                </ul>
              </div>
              <div className="d-flex justify-content-end">
                <ButtonWithLoading
                  variant="outline-primary"
                  loading={exportingLayout}
                  onClick={handleExportLayout}
                >
                  Exportar Layout
                </ButtonWithLoading>
              </div>
            </div>
          </Tab>
        </Tabs>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose} disabled={uploading}>
          Cancelar
        </Button>
        <ButtonWithLoading
          variant="primary"
          onClick={handleSubmit}
          loading={uploading ? 'true' : undefined}
        >
          Importar
        </ButtonWithLoading>
      </Modal.Footer>
    </Modal>
  );
};

export default ImportarDepositoNotasModal;
